import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import getConditionalData from '../utils/query/getConditionalData';
import getConditionalDataFlag from '../utils/query/getConditionalDataFlag';
import normalizeQueryResults from '../utils/query/normalizeQueryResults';
import ShareclassCardLabelValue from './ShareclassCardLabelValue';
import Icon from './Icon';
import { computed } from 'mobx';
import getQueryResults from '../utils/query/getQueryResults';
import Tooltip from './Tooltip';

const ShareclassCardWrapper = styled<any, any>('div')`
    ${propStyles};
`;

ShareclassCardWrapper.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    margin: 'md',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    boxShadow: 'sm',
};

export const ShareclassCardList = styled<any, any>('ul')`
    ${propStyles};
`;

ShareclassCardList.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    paddingX: 'md',
};

export const ShareclassCardRow = styled<any, any>('li')`
    ${propStyles};
    position: relative;
    list-style: none;
`;

ShareclassCardRow.defaultProps = {
    display: 'flex',
    flex: '1 0 auto',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    paddingY: 'md',
    fontSize: 'sm',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
};

const ExpandIcon = styled<any, any>(Icon)`
    transition: transform 300ms ease-in-out;
    &.open {
        transform: rotate3d(1, 0, 0, 180deg);
    }
`;

ExpandIcon.defaultProps = {
    color: 'primary',
};

const ShareclassCardExpandButton = styled<any, any>('button')`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

ShareclassCardExpandButton.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginX: 'md',
    marginY: 'md',
    borderStyle: 'none',
    borderWidth: '0',
    bgColor: 'transparent',
};

@observer
class ShareclassCards extends Component<any, any> {
    @computed
    get checkEnableByFundList() {
        const { shareClass, rowExpanderConfig, dataStore } = this.props;
        const fundListCheck =
            rowExpanderConfig && rowExpanderConfig.fundListCheck;
        if (fundListCheck) {
            return dataStore.fundListContainsEntity(shareClass, fundListCheck);
        }
        return true;
    }

    @computed
    get isRowExpandDisabled() {
        if (!this.checkEnableByFundList || this.isShareclassDisabled) {
            return true;
        }
        return false;
    }

    @computed
    get isShareclassDisabled() {
        const { shareClass, rowExpanderConfig } = this.props;
        if (rowExpanderConfig && rowExpanderConfig.disabledFlag) {
            const conditionalConfig = rowExpanderConfig.disabledFlag;
            const values = normalizeQueryResults({
                ...shareClass.properties_pub,
                ...getConditionalData(shareClass, conditionalConfig),
            });
            return getConditionalDataFlag(values, conditionalConfig);
        }
        return false;
    }

    @computed
    get disabledTooltipDisclaimer() {
        const { dataStore, rowExpanderConfig, queryContext } = this.props;
        if (rowExpanderConfig.disabledTooltipDisclaimer) {
            const disclaimerQuery = {
                disclaimer: rowExpanderConfig.disabledTooltipDisclaimer,
            };
            const data = getQueryResults(
                queryContext,
                disclaimerQuery,
                dataStore.culture
            );
            if (data && data.disclaimer) {
                return data.disclaimer;
            }
        }
        return;
    }

    @computed
    get cardExpander() {
        const {
            theme,
            expanded,
            rowExpanderConfig,
            shareClass,
            rowType,
            toggleExpanded,
        } = this.props;

        let rowExpandable = true;
        if (rowExpanderConfig && rowExpanderConfig.disabledFlag) {
            const conditionalConfig = rowExpanderConfig.disabledFlag;
            if (typeof conditionalConfig === 'boolean') {
                rowExpandable = !conditionalConfig;
            } else {
                const values = normalizeQueryResults({
                    ...shareClass.properties_pub,
                    ...getConditionalData(shareClass, conditionalConfig),
                });
                rowExpandable = !getConditionalDataFlag(
                    values,
                    conditionalConfig
                );
            }
        }

        if (rowExpandable && !this.isRowExpandDisabled) {
            if (rowType !== 'documents') {
                const iconFileName = expanded
                    ? (rowExpanderConfig &&
                          rowExpanderConfig.mobileCollapseIconFileName) ||
                      'MobileCollapseIcon'
                    : (rowExpanderConfig &&
                          rowExpanderConfig.mobileExpandIconFileName) ||
                      'MobileExpandIcon';

                return (
                    <ShareclassCardExpandButton
                        onClick={toggleExpanded}
                        {...theme.ShareclassCardExpandButton}
                    >
                        <Icon
                            iconSize="1.4em"
                            fileName={iconFileName}
                            {...theme.ShareclassCardExpandIcon}
                            accessibilityContext={`for share class identified by ${shareClass.clientCode}`}
                        />
                    </ShareclassCardExpandButton>
                );
            }
        }
        if (this.isRowExpandDisabled) {
            const disabledTooltip = this.disabledTooltipDisclaimer;
            if (disabledTooltip) {
                return (
                    <ShareclassCardExpandButton
                        {...theme.ShareclassCardExpandButtonDisabled}
                    >
                        <Tooltip
                            content={[disabledTooltip]}
                            accessibilityContext={`for disabled share class identified by ${shareClass.clientCode}`}
                        />
                    </ShareclassCardExpandButton>
                );
            }
        }
    }

    render() {
        const {
            theme,
            alternated,
            rowType,
            rowHasExpandableOptions,
            setSelectedGroup,
            selectedGroupMap,
            children,
            rowExpanderConfig,
            dataStore,
            queryContext,
            ...shareClassProp
        } = this.props;

        const rowExpandable = !this.isRowExpandDisabled;
        const shareclassCardWrapperTheme = rowExpandable
            ? theme.ShareclassCardWrapper
            : {
                  ...theme.ShareclassCardWrapper,
                  ...theme.ShareclassCardWrapperDisabled,
              };

        const shareclassCardListTheme = rowExpandable
            ? theme.ShareclassCardList
            : {
                  ...theme.ShareclassCardList,
                  ...theme.ShareclassCardListDisabled,
              };

        const shareclassCardRowHasSubListTheme = rowExpandable
            ? theme.ShareclassCardRowHasSubList
            : {
                  ...theme.ShareclassCardRowHasSubList,
                  ...theme.ShareclassCardRowHasSubListDisabled,
              };

        const shareclassCardRowTheme = rowExpandable
            ? theme.ShareclassCardRow
            : {
                  ...theme.ShareclassCardRow,
                  ...theme.ShareclassCardRowDisabled,
              };

        const contentPosition =
            (rowExpanderConfig && rowExpanderConfig.contentPosition) ||
            'default';
        return (
            <ShareclassCardWrapper
                data-alternated={alternated}
                {...shareclassCardWrapperTheme}
            >
                <ShareclassCardList {...shareclassCardListTheme}>
                    {shareClassProp.columns.map((column, index) => {
                        const rowTheme =
                            ['group', 'dropdown'].includes(column.type) &&
                            shareclassCardRowHasSubListTheme
                                ? shareclassCardRowHasSubListTheme
                                : shareclassCardRowTheme;
                        return (
                            <Fragment key={`${column.label}-${index}`}>
                                <ShareclassCardRow
                                    onClick={
                                        column.type === 'download'
                                            ? shareClassProp.toggleExpanded
                                            : () => null
                                    }
                                    cursor={
                                        column.type === 'download'
                                            ? 'pointer'
                                            : 'auto'
                                    }
                                    {...rowTheme}
                                >
                                    <ShareclassCardLabelValue
                                        shareClass={shareClassProp}
                                        column={column}
                                        columnIndex={index}
                                        setSelectedGroup={setSelectedGroup}
                                        selectedGroupMap={selectedGroupMap}
                                        queryContext={queryContext}
                                        expanded={shareClassProp.expanded}
                                    />
                                </ShareclassCardRow>
                                {shareClassProp.expanded &&
                                    rowExpandable &&
                                    contentPosition === 'inline' &&
                                    column.type === 'download' &&
                                    children}
                            </Fragment>
                        );
                    })}
                </ShareclassCardList>
                {shareClassProp.expanded &&
                    rowExpandable &&
                    contentPosition !== 'inline' &&
                    children}
                {this.cardExpander}
            </ShareclassCardWrapper>
        );
    }
}

export default withTheme(ShareclassCards) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassCards.tsx